import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { SketchPicker } from 'react-color';
import fontColor from '../fontColor';
import LogoUploadField from './LogoUploadField';
import ThemePreviewCard from './ThemePreviewCard';

/**
 * ช่องเลือกสี (แถบ swatch + popover SketchPicker) ใช้ซ้ำสำหรับสีหลัก/สีรอง
 * เดิมสอง block นี้ก็อปกันแทบทั้งหมด ต่างแค่ label กับ z-index/top ของ popover
 */
const ColorSwatchPicker = ({ label, color, isOpen, onToggle, onColorChange, rowExtraStyle, popoverExtraStyle }) => (
    <>
        <p>{label}</p>
        <div
            className="row"
            style={{
                background: '#fff',
                border: '1px #EEEEEE solid',
                borderRadius: '6px',
                marginBottom: '20px',
                marginLeft: '2px',
                cursor: 'pointer',
                padding: '5px',
                width: '352px',
                height: '36px',
                color,
                ...rowExtraStyle,
            }}
            onClick={onToggle}
        >
            <div
                style={{
                    background: color,
                    borderRadius: '2px',
                    height: '20px',
                    width: '16px',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                }}
            />
            &nbsp;&nbsp;{color}
        </div>
        <div style={{ position: 'absolute', zIndex: '2', ...popoverExtraStyle }}>
            {isOpen ? <SketchPicker color={color} onChange={(c) => onColorChange(c.hex)} /> : null}
        </div>
    </>
);

/**
 * Modal ตั้งค่าธีม (ชื่อธีม, โลโก้, สีหลัก/สีรอง, พรีวิว, ปุ่มยกเลิก/บันทึก)
 * เดิม ~600 บรรทัด/ไฟล์ ซ้ำกันเกือบทั้งหมดระหว่าง CreatePortal.jsx และ EditPortal.jsx
 * ความต่างจริงที่คงไว้ผ่าน props:
 * - โลโก้: Edit เก็บเป็น base64 string, Create เก็บเป็น File object — ผู้เรียกคำนวณ
 *   logoSrc/hasLogo เอง ที่นี่ไม่ยุ่งกับวิธีเก็บ state
 * - closeOtherPickerOnOpen: Create ปิด picker อีกฝั่งเมื่อเปิดอีกอัน, Edit ไม่ปิด —
 *   เป็นพฤติกรรมที่ต่างกันจริงในโค้ดเดิม (ดูรายละเอียดตอนเรียกใช้)
 * - ปุ่ม "รีเซ็ตเป็นธีมเริ่มต้น" ใช้สี committedPrimaryColor (ธีมที่ยังไม่กด "บันทึก")
 *   ไม่ใช่สีที่กำลัง pick อยู่ — ตามพฤติกรรมเดิมทั้งสองไฟล์
 */
const ThemeEditorModal = ({
    open,
    onClose,
    themeName,
    onThemeNameChange,
    primaryColorPick,
    onPrimaryColorPick,
    secondaryColorPick,
    onSecondaryColorPick,
    primaryPickerOpen,
    onTogglePrimaryPicker,
    secondaryPickerOpen,
    onToggleSecondaryPicker,
    closeOtherPickerOnOpen,
    hasLogo,
    logoSrc,
    onLogoChange,
    onLogoClear,
    logoAccept,
    onResetTheme,
    slidePage,
    setSlidePage,
    slide1NavTop,
    committedPrimaryColor,
    onRequestCancel,
    onCommitPrimaryColor,
    onCommitSecondaryColor,
}) => {
    const handleTogglePrimaryPicker = () => {
        onTogglePrimaryPicker(!primaryPickerOpen);
        if (closeOtherPickerOnOpen) onToggleSecondaryPicker(false);
    };

    const handleToggleSecondaryPicker = () => {
        onToggleSecondaryPicker(!secondaryPickerOpen);
        if (closeOtherPickerOnOpen) onTogglePrimaryPicker(false);
    };

    const handleSave = () => {
        onClose();
        onCommitPrimaryColor(primaryColorPick);
        onCommitSecondaryColor(secondaryColorPick);
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '1107px',
                    height: '712px',
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    p: 5,
                    overflow: 'auto',
                }}
            >
                <div>
                    <div className="row">
                        <div className="col-md-12 col-lg-6">
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ marginBottom: '40px' }}>
                                    <Typography
                                        id="modal-modal-title"
                                        variant="h6"
                                        component="h2"
                                        style={{ fontWeight: 'bold', marginBottom: '30px' }}
                                    >
                                        กำหนดธีม
                                    </Typography>

                                    <p>ชื่อธีม*</p>
                                    <input
                                        value={themeName}
                                        style={{
                                            width: '352px',
                                            height: '36px',
                                            borderRadius: '6px',
                                            marginBottom: '20px',
                                            border: '1px solid #EEEEEE',
                                            color: 'black',
                                            padding: '12px',
                                        }}
                                        onChange={(e) => onThemeNameChange(e.target.value.startsWith(' ') ? '' : e.target.value)}
                                        placeholder="ธีมเริ่มต้น"
                                    />

                                    <p>โลโก้</p>
                                    <LogoUploadField
                                        hasLogo={hasLogo}
                                        logoSrc={logoSrc}
                                        onChange={onLogoChange}
                                        onClear={onLogoClear}
                                        accept={logoAccept}
                                    />

                                    <ColorSwatchPicker
                                        label="สีหลัก"
                                        color={primaryColorPick}
                                        isOpen={primaryPickerOpen}
                                        onToggle={handleTogglePrimaryPicker}
                                        onColorChange={onPrimaryColorPick}
                                    />

                                    <ColorSwatchPicker
                                        label="สีรอง"
                                        color={secondaryColorPick}
                                        isOpen={secondaryPickerOpen}
                                        onToggle={handleToggleSecondaryPicker}
                                        onColorChange={onSecondaryColorPick}
                                        rowExtraStyle={{ zIndex: '1' }}
                                        popoverExtraStyle={{ top: '37.2%' }}
                                    />

                                    <div>
                                        <Button
                                            variant="outlined"
                                            style={{
                                                marginTop: '15px',
                                                width: '352px',
                                                height: '36px',
                                                marginRight: '10px',
                                                backgroundColor: committedPrimaryColor,
                                                color: fontColor(committedPrimaryColor),
                                                zIndex: '1',
                                                border: 'none',
                                            }}
                                            onClick={onResetTheme}
                                        >
                                            รีเซ็ตเป็นธีมเริ่มต้น
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-12 col-lg-6">
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '520px', marginBottom: '30px' }}>
                                    <div>
                                        <p style={{ fontSize: '16px' }}>ตัวอย่างการแสดงเว็บไซต์</p>
                                    </div>
                                    <ThemePreviewCard
                                        slidePage={slidePage}
                                        setSlidePage={setSlidePage}
                                        primaryColor={primaryColorPick}
                                        secondaryColor={secondaryColorPick}
                                        logoSrc={logoSrc}
                                        slide1NavTop={slide1NavTop}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            onClick={onRequestCancel}
                            variant="outlined"
                            style={{
                                fontSize: '16px',
                                width: '156px',
                                height: '36px',
                                marginRight: '32px',
                                color: committedPrimaryColor,
                                borderColor: committedPrimaryColor,
                                borderRadius: '6px',
                            }}
                        >
                            ยกเลิก
                        </Button>
                        <Button
                            style={{
                                fontSize: '16px',
                                width: '156px',
                                height: '36px',
                                marginRight: '10px',
                                color: fontColor(committedPrimaryColor),
                                borderColor: committedPrimaryColor,
                                background: committedPrimaryColor,
                                borderRadius: '6px',
                            }}
                            onClick={handleSave}
                        >
                            บันทึก
                        </Button>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

export default ThemeEditorModal;